"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { deliveryIntegrationsQueryKey } from "@/hooks/queries/delivery-integrations"
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import { agentIntegrationStatusesTenantQueryKey } from "@/hooks/queries/agent-integration-status-keys"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  connectDeliveryIntegrationAction,
  disconnectDeliveryIntegrationAction,
  testDeliveryIntegrationAction,
  toggleDeliveryIntegrationAction,
  updateDeliveryIntegrationAction,
  type ConnectDeliveryIntegrationInput,
  type DeliveryIntegrationView,
  type ToggleDeliveryIntegrationInput,
  type UpdateDeliveryIntegrationInput,
} from "@/server-actions/delivery-integrations"
import { useTranslations } from "next-intl"


type FormErrorSetter = (errors: Record<string, unknown>) => void

export type ConnectDeliveryIntegrationMutationInput =
  ConnectDeliveryIntegrationInput & { setErrors?: FormErrorSetter }

export type UpdateDeliveryIntegrationMutationInput =
  UpdateDeliveryIntegrationInput & { setErrors?: FormErrorSetter }

export function useConnectDeliveryIntegrationMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryIntegrations")

  const cache = useDeliveryIntegrationMutationCache()

  return useMutation({
    mutationFn: async ({ setErrors, ...input }:
      ConnectDeliveryIntegrationMutationInput): Promise<DeliveryIntegrationView> => {
      const result = await withQueryHandling(
        connectDeliveryIntegrationAction(input),
        {
          loading: tUi("connectingAndTestingDeliveryIntegration"),
          success: value => integrationSuccessMessage(
            value as DeliveryIntegrationView, tUi,
          ),
          setErrors,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.upsert,
  })
}

export function useUpdateDeliveryIntegrationMutation() {
  const tUi = useTranslations("Feedback.MutationsDeliveryIntegrations")

  const withQueryHandling = useTranslatedQueryHandling()

  const cache = useDeliveryIntegrationMutationCache()

  return useMutation({
    mutationFn: async ({ setErrors, ...input }:
      UpdateDeliveryIntegrationMutationInput): Promise<DeliveryIntegrationView> => {
      const result = await withQueryHandling(
        updateDeliveryIntegrationAction(input),
        {
          loading: input.apiKey || input.config
            ? tUi("savingAndTestingDeliveryIntegration")
            : tUi("savingDeliveryIntegration"),
          success: value => integrationSuccessMessage(
            value as DeliveryIntegrationView, tUi,
          ),
          setErrors,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.upsert,
  })
}

export function useTestDeliveryIntegrationMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryIntegrations")

  const cache = useDeliveryIntegrationMutationCache()

  return useMutation({
    mutationFn: async (integrationId: string): Promise<DeliveryIntegrationView> => {
      const result = await withQueryHandling(
        testDeliveryIntegrationAction({ integrationId }),
        {
          loading: tUi("testingDeliveryIntegration"),
          success: value => integrationSuccessMessage(
            value as DeliveryIntegrationView, tUi,
          ),
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.upsert,
  })
}

export function useToggleDeliveryIntegrationMutation() {
  const tUi = useTranslations("Feedback.MutationsDeliveryIntegrations")

  const withQueryHandling = useTranslatedQueryHandling()

  const cache = useDeliveryIntegrationMutationCache()

  return useMutation({
    mutationFn: async (
      input: ToggleDeliveryIntegrationInput,
    ): Promise<DeliveryIntegrationView> => {
      const result = await withQueryHandling(
        toggleDeliveryIntegrationAction(input),
        {
          loading: input.active
            ? tUi("enablingDeliveryIntegration")
            : tUi("disablingDeliveryIntegration"),
          success: input.active
            ? tUi("deliveryIntegrationEnabled")
            : tUi("deliveryIntegrationDisabled"),
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: cache.upsert,
  })
}

export function useDisconnectDeliveryIntegrationMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryIntegrations")

  const cache = useDeliveryIntegrationMutationCache()

  return useMutation({
    mutationFn: async (integrationId: string): Promise<{ id: string }> => {
      const result = await withQueryHandling(
        disconnectDeliveryIntegrationAction({ integrationId }),
        {
          loading: tUi("disconnectingDeliveryIntegration"),
          success: tUi("deliveryIntegrationDisconnected"),
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: ({ id }) => cache.remove(id),
  })
}

function useDeliveryIntegrationMutationCache() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getQueryTenantId(user)
  const queryKey = deliveryIntegrationsQueryKey(tenantId)

  const invalidateAgentStatuses = () => queryClient.invalidateQueries({
    queryKey: agentIntegrationStatusesTenantQueryKey(tenantId),
  })

  return {
    upsert: (integration: DeliveryIntegrationView) => {
      queryClient.setQueryData<DeliveryIntegrationView[]>(queryKey, current => {
        if (!current) return [integration]
        const index = current.findIndex(item => item.id === integration.id)
        if (index < 0) return [integration, ...current]
        return current.map(item => item.id === integration.id ? integration : item)
      })
      void invalidateAgentStatuses()
    },
    remove: (integrationId: string) => {
      queryClient.setQueryData<DeliveryIntegrationView[]>(queryKey, current =>
        current?.filter(item => item.id !== integrationId),
      )
      void invalidateAgentStatuses()
    },
  }
}

function integrationSuccessMessage(integration: DeliveryIntegrationView, tUi: ReturnType<typeof useTranslations<"Feedback.MutationsDeliveryIntegrations">>): string {
  const providerName = deliveryProviderName(integration.provider)
  return integration.verificationStatus === "succeeded"
    ? tUi("providerConnected", {provider: providerName})
    : tUi("providerVerificationFailed", {provider: providerName})
}

function deliveryProviderName(
  provider: DeliveryIntegrationView["provider"],
): string {
  switch (provider) {
    case "nova_post":
      return "Nova Post"
    default: {
      const exhaustiveProvider: never = provider
      return exhaustiveProvider
    }
  }
}
